import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useCompany } from './CompanyContext';
import { useImpersonation } from './ImpersonationContext';
import jsPDF from 'jspdf';
import type { AuditAction, AuditEntityType } from './auditLog';
import { hasPlanAccess, PLAN_FEATURES } from './lib/pricing';
import UpgradeWall from './components/UpgradeWall';
import './Audit.css';
import { hoyEc } from './lib/fechas';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AuditLog {
  id: string;
  user_name: string;
  action: AuditAction;
  entity_type: AuditEntityType;
  entity_id: string;
  entity_name: string;
  details: Record<string, unknown> | null;
  created_at: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MODULE_LABELS: Record<string, string> = {
  product:    'Productos',
  warehouse:  'Bodegas',
  zone:       'Zonas',
  user:       'Usuarios',
  invitation: 'Invitaciones',
  sso_document: 'Documentos SSO',
  work_permit:  'Permisos de trabajo',
  work_permit_template: 'Preguntas propias de permisos',
  checklist_item: 'Checklist Anexo 1',
  emergency_plan: 'Plan de emergencias',
  // PE-B2b: nombres en español en vez de los técnicos.
  emergency_drill: 'Simulacros',
  emergency_drill_annual_report: 'Informes anuales de simulacros',
  job_position: 'Puestos de trabajo',
  company_site: 'Sedes',   // F1a (M40)
  worker: 'Trabajadores',  // F1a2
  inspection: 'Inspecciones',   // F3a (M41): firma y reapertura
  signed_record: 'Registros firmados',   // M45: asistencia, entrega de EPP y aptitud médica
};

// Nombre en singular para la descripción ("Editó simulacro: …"); si no está, se quita la "s" final del módulo.
const SINGULAR: Record<string, string> = {
  emergency_drill: 'simulacro',
  emergency_drill_annual_report: 'informe anual de simulacros',
  job_position: 'puesto de trabajo',
  company_site: 'sede',
  worker: 'trabajador',
  inspection: 'inspección',
  signed_record: 'registro firmado',
};

const ACTION_LABELS: Record<string, string> = {
  created: 'Creado',
  updated: 'Editado',
  deleted: 'Eliminado',
  viewed:  'Visto',
  user_login:       'Inicio de sesión',
  user_logout:      'Cierre de sesión',
  password_changed: 'Cambio de contraseña',
};

// Registros de sesión (entity_type 'auth'): fuera del filtro "Módulo"; se
// muestran solo con "Incluir inicios de sesión" (§17 ítem 73).
const AUTH_LABEL = 'Sesión';
const PAGE_SIZE  = 100;

interface AuditSummary {
  total:    number;
  created:  number;
  updated:  number;
  deleted:  number;
  usuarios: string[];
}

const EMPTY_SUMMARY: AuditSummary = { total: 0, created: 0, updated: 0, deleted: 0, usuarios: [] };

const ACTION_VERBS: Record<string, string> = {
  created: 'Creó',
  updated: 'Editó',
  deleted: 'Eliminó',
  viewed:  'Vio',
};

const ACTION_COLORS: Record<string, { color: string; bg: string }> = {
  created: { color: '#4ade80', bg: 'rgba(34,197,94,0.12)'   },
  updated: { color: '#60a5fa', bg: 'rgba(59,130,246,0.12)'  },
  deleted: { color: '#f87171', bg: 'rgba(239,68,68,0.12)'   },
  viewed:  { color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
};

// ── Utils ─────────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('es-EC', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export function buildDescription(log: AuditLog): string {
  if ((log.entity_type as string) === 'auth') return `${ACTION_LABELS[log.action] ?? log.action}: ${log.entity_name}`;
  const verb  = ACTION_VERBS[log.action] ?? log.action;
  const mod   = MODULE_LABELS[log.entity_type] ?? log.entity_type;
  const base  = `${verb} ${SINGULAR[log.entity_type] ?? mod.toLowerCase().replace(/s$/, '')}: "${log.entity_name}"`;
  if (!log.details || Object.keys(log.details).length === 0) return base;
  // F3a2: la reapertura de una inspección firmada (reabrir_inspeccion, M41) dice "Reabrió" y muestra el motivo.
  if (log.details.evento === 'inspeccion_reabierta') {
    const motivo = typeof log.details.motivo === 'string' ? ` (motivo: ${log.details.motivo})` : '';
    return `Reabrió ${SINGULAR[log.entity_type] ?? 'inspección'}: "${log.entity_name}"${motivo}`;
  }
  // M45: la reapertura de un registro firmado (reabrir_registro_firmado) dice "Reabrió" y muestra el motivo.
  if (log.details.evento === 'registro_firmado_reabierto') {
    const motivo = typeof log.details.motivo === 'string' ? ` (motivo: ${log.details.motivo})` : '';
    return `Reabrió ${SINGULAR[log.entity_type] ?? 'registro firmado'}: "${log.entity_name}"${motivo}`;
  }
  if (log.details.evento === 'registro_firmado') return `Firmó ${SINGULAR[log.entity_type] ?? 'registro'}: "${log.entity_name}"`;
  if (log.details.evento === 'registro_preparado') return `Preparó para firma: "${log.entity_name}"`;
  const extras: string[] = [];
  if (log.details.role)   extras.push(`rol: ${log.details.role}`);
  if (log.details.active !== undefined)
    extras.push(log.details.active ? 'activado' : 'desactivado');
  if (log.details.product_count !== undefined)
    extras.push(`${log.details.product_count} productos`);
  // PE-B2b: la reapertura de un simulacro muestra su motivo.
  if (log.details.evento === 'simulacro_reabierto' && typeof log.details.motivo === 'string')
    extras.push(`reabierto, motivo: ${log.details.motivo}`);
  // F1a2: trabajadores muestran puesto, sede y si se desvinculó.
  if (log.entity_type === 'worker') {
    if (typeof log.details.puesto === 'string') extras.push(`puesto: ${log.details.puesto}`);
    if (typeof log.details.sede === 'string') extras.push(`sede: ${log.details.sede}`);
    if (log.details.estado === 'desvinculado') extras.push('desvinculado');
  }
  return extras.length ? `${base} (${extras.join(', ')})` : base;
}

function stripAccents(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Audit() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const effectiveCompany   = impersonated ?? company;
  const effectiveCompanyId = effectiveCompany?.id ?? null;
  const isSuperAdmin       = profile?.role === 'superadmin';

  const [logs,      setLogs]      = useState<AuditLog[]>([]);
  const [summary,   setSummary]   = useState<AuditSummary>(EMPTY_SUMMARY);
  const [loading,   setLoading]   = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestRef = useRef(0);
  const [generating, setGenerating] = useState(false);

  // filters
  const [filterModule, setFilterModule] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterUser,   setFilterUser]   = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [incluirAuth,  setIncluirAuth]  = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  // Página de registros + conteos reales en la base (antes: tope de 500 y
  // contadores calculados solo sobre lo cargado; §17 ítem 73).
  const filtros = useCallback(() => ({
    p_company_id:   effectiveCompanyId,
    p_action:       filterAction || null,
    p_entity_type:  filterModule || null,
    p_user_name:    filterUser || null,
    p_date_from:    dateFrom ? dateFrom + 'T00:00:00' : null,
    p_date_to:      dateTo ? dateTo + 'T23:59:59' : null,
    p_incluir_auth: incluirAuth,
  }), [effectiveCompanyId, filterAction, filterModule, filterUser, dateFrom, dateTo, incluirAuth]);

  const fetchLogs = useCallback(async () => {
    if (!effectiveCompanyId) { setLoading(false); return; }
    const req = ++requestRef.current;
    setLoading(true);
    const [{ data, error }, { data: sum, error: sumErr }] = await Promise.all([
      supabase.rpc('get_visible_audit_logs_page', { ...filtros(), p_limit: PAGE_SIZE, p_offset: 0 }),
      supabase.rpc('get_visible_audit_summary', filtros()),
    ]);
    if (req !== requestRef.current) return;
    if (error) console.error('Error al cargar el historial:', error.message);
    if (sumErr) console.error('Error al contar el historial:', sumErr.message);
    setLogs(error ? [] : ((data as AuditLog[]) ?? []));
    setSummary(sumErr ? EMPTY_SUMMARY : { ...EMPTY_SUMMARY, ...(sum as Partial<AuditSummary>) });
    setLoading(false);
  }, [effectiveCompanyId, filtros]);

  const loadMore = async () => {
    if (!effectiveCompanyId || loadingMore) return;
    const req = requestRef.current;
    setLoadingMore(true);
    const { data, error } = await supabase.rpc('get_visible_audit_logs_page', {
      ...filtros(), p_limit: PAGE_SIZE, p_offset: logs.length,
    });
    if (req === requestRef.current) {
      if (error) console.error('Error al cargar más registros:', error.message);
      else setLogs(prev => [...prev, ...((data as AuditLog[]) ?? [])]);
    }
    setLoadingMore(false);
  };

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // ── Derived ────────────────────────────────────────────────────────────────

  const allUsers = summary.usuarios;
  const counts   = { created: summary.created, updated: summary.updated, deleted: summary.deleted };
  const hasMore  = logs.length < summary.total;

  // ── PDF Export ─────────────────────────────────────────────────────────────

  const exportPdf = async () => {
    setGenerating(true);
    try {
      const pdf  = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw   = pdf.internal.pageSize.getWidth();
      const ph   = pdf.internal.pageSize.getHeight();
      const M    = 14;
      const UW   = pw - 2 * M;
      const FTR  = 9;
      let y      = 0;
      let pageNum = 1;

      const fill   = (r: number, g: number, b: number) => pdf.setFillColor(r, g, b);
      const color  = (r: number, g: number, b: number) => pdf.setTextColor(r, g, b);
      const font   = (style: 'normal'|'bold', size: number) => { pdf.setFont('helvetica', style); pdf.setFontSize(size); };
      const rect   = (x: number, ry: number, w: number, h: number) => pdf.rect(x, ry, w, h, 'F');
      const txt    = (s: string, x: number, ty: number, opts?: object) => pdf.text(s, x, ty, opts ?? {});

      const addFooter = () => {
        fill(15, 23, 42); rect(0, ph - FTR, pw, FTR);
        fill(34, 197, 94); rect(0, ph - FTR, pw, 0.4);
        font('normal', 6); color(100, 116, 139);
        txt('StorSafe - Historial de Cambios', M, ph - 3);
        txt(`Pag. ${pageNum}`, pw - M, ph - 3, { align: 'right' });
      };

      const addHeader = () => {
        fill(15, 23, 42); rect(0, 0, pw, 12);
        fill(34, 197, 94); rect(0, 11.5, pw, 0.5);
        font('bold', 9); color(34, 197, 94); txt('HISTORIAL DE CAMBIOS - StorSafe', M, 8);
        font('normal', 7); color(100, 116, 139);
        const subtitle = [
          effectiveCompany?.name,
          filterModule ? MODULE_LABELS[filterModule] : null,
          filterAction ? ACTION_LABELS[filterAction] : null,
        ].filter(Boolean).join(' · ');
        txt(subtitle, M + 60, 8);
        txt(`Generado: ${new Date().toLocaleDateString('es-EC')}`, pw - M, 8, { align: 'right' });
      };

      const checkPage = (needed: number) => {
        if (y + needed > ph - FTR - 4) {
          addFooter(); pageNum++; pdf.addPage(); addHeader(); y = 18;
        }
      };

      // Cover header
      addHeader();
      y = 18;

      // Summary
      font('bold', 9); color(226, 232, 240);
      txt(`Registros: ${logs.length} de ${summary.total}`, M, y + 5);
      font('normal', 8); color(100, 116, 139);
      txt(`Creados: ${counts.created}   Editados: ${counts.updated}   Eliminados: ${counts.deleted}`, M + 46, y + 5);
      y += 12;

      // Table header
      const drawTableHeader = () => {
        fill(15, 23, 42); rect(M, y, UW, 7);
        fill(34, 197, 94); rect(M, y, 3, 7);
        font('bold', 6.5); color(71, 85, 105);
        txt('FECHA/HORA',  M + 6,   y + 5);
        txt('USUARIO',     M + 46,  y + 5);
        txt('ACCION',      M + 90,  y + 5);
        txt('MODULO',      M + 118, y + 5);
        txt('DESCRIPCION', M + 148, y + 5);
        y += 7;
      };
      drawTableHeader();

      for (let i = 0; i < logs.length; i++) {
        checkPage(7);
        const log = logs[i];
        if (i === 0 || (y <= 18 + 7 && pageNum > 1)) {
          // after page break, drawTableHeader was already called by checkPage
        }
        fill(i % 2 === 0 ? 17 : 22, i % 2 === 0 ? 24 : 30, i % 2 === 0 ? 39 : 46);
        rect(M, y, UW, 6.5);
        font('normal', 6.5); color(148, 163, 184);
        txt(fmtDate(log.created_at), M + 6, y + 4.5);
        color(203, 213, 225);
        txt(stripAccents(log.user_name.slice(0, 20)),                    M + 46,  y + 4.5);
        const ac = ACTION_LABELS[log.action] ?? log.action;
        txt(stripAccents(ac),                                            M + 90,  y + 4.5);
        txt(stripAccents((MODULE_LABELS[log.entity_type] ?? log.entity_type).slice(0, 16)), M + 118, y + 4.5);
        txt(stripAccents(buildDescription(log).slice(0, 58)),            M + 148, y + 4.5);
        y += 6.5;
      }

      addFooter();
      pdf.save(`StorSafe_Auditoria_${hoyEc()}.pdf`);
    } finally {
      setGenerating(false);
    }
  };

  // ── Plan gate (after all hooks) ──────────────────────────────────────────────

  if (!isSuperAdmin && effectiveCompany && !hasPlanAccess(effectiveCompany.plan, 'auditoria')) {
    return <UpgradeWall {...PLAN_FEATURES.auditoria} />;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const hasActiveFilters = filterModule || filterAction || filterUser || dateFrom || dateTo || incluirAuth;

  const clearFilters = () => {
    setFilterModule(''); setFilterAction(''); setFilterUser('');
    setDateFrom(''); setDateTo(''); setIncluirAuth(false);
  };

  return (
    <div className="aud-root">

      {/* ── Header ── */}
      <div className="aud-header">
        <div>
          <h3 className="aud-title">Historial de Cambios</h3>
          <p className="aud-subtitle">
            {loading
              ? 'Cargando…'
              : `${summary.total} registro${summary.total !== 1 ? 's' : ''} encontrado${summary.total !== 1 ? 's' : ''}${hasMore ? ` · mostrando ${logs.length}` : ''}`}
          </p>
        </div>
        <button
          className="aud-btn-export"
          onClick={exportPdf}
          disabled={generating || logs.length === 0}
        >
          {generating
            ? <><span className="aud-spinner" /> Generando…</>
            : <><PdfIcon /> Exportar PDF</>
          }
        </button>
      </div>

      {/* ── Stats row ── */}
      <div className="aud-stats-row">
        <div className="aud-stat green">
          <span className="aud-stat-val">{counts.created}</span>
          <span className="aud-stat-lbl">Creados</span>
        </div>
        <div className="aud-stat blue">
          <span className="aud-stat-val">{counts.updated}</span>
          <span className="aud-stat-lbl">Editados</span>
        </div>
        <div className="aud-stat red">
          <span className="aud-stat-val">{counts.deleted}</span>
          <span className="aud-stat-lbl">Eliminados</span>
        </div>
        <div className="aud-stat gray">
          <span className="aud-stat-val">{summary.total}</span>
          <span className="aud-stat-lbl">Total</span>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="aud-filters">
        <div className="aud-filter-group">
          <label className="aud-filter-label">Módulo</label>
          <select className="aud-select" value={filterModule} onChange={e => setFilterModule(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(MODULE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        <div className="aud-filter-group">
          <label className="aud-filter-label">Acción</label>
          <select className="aud-select" value={filterAction} onChange={e => setFilterAction(e.target.value)}>
            <option value="">Todas</option>
            <option value="created">Creado</option>
            <option value="updated">Editado</option>
            <option value="deleted">Eliminado</option>
          </select>
        </div>

        <div className="aud-filter-group">
          <label className="aud-filter-label">Usuario</label>
          <select className="aud-select" value={filterUser} onChange={e => setFilterUser(e.target.value)}>
            <option value="">Todos</option>
            {allUsers.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>

        <div className="aud-filter-group">
          <label className="aud-filter-label">Desde</label>
          <input
            type="date"
            className="aud-date-input"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
        </div>

        <div className="aud-filter-group">
          <label className="aud-filter-label">Hasta</label>
          <input
            type="date"
            className="aud-date-input"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
        </div>

        <div className="aud-filter-group">
          <label className="aud-filter-label">Sesiones</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#cbd5e1', fontSize: '0.82rem', cursor: 'pointer', minHeight: 34 }}>
            <input type="checkbox" checked={incluirAuth} onChange={e => setIncluirAuth(e.target.checked)} />
            Incluir inicios de sesión
          </label>
        </div>

        {hasActiveFilters && (
          <button className="aud-btn-clear" onClick={clearFilters} title="Limpiar filtros">
            <CloseIcon /> Limpiar
          </button>
        )}
      </div>

      {/* ── Table ── */}
      <div className="aud-table-wrap">
        {loading ? (
          <div className="aud-loading">
            <span className="aud-spinner aud-spinner-lg" />
            <p>Cargando registros…</p>
          </div>
        ) : !effectiveCompanyId ? (
          <div className="aud-empty">
            <HistoryIcon />
            <p>Selecciona una empresa para ver su auditoría.</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="aud-empty">
            <HistoryIcon />
            <p>{hasActiveFilters ? 'Sin registros con los filtros aplicados.' : 'No hay registros de auditoría aún.'}</p>
            {hasActiveFilters && (
              <button className="aud-btn-clear-sm" onClick={clearFilters}>Limpiar filtros</button>
            )}
          </div>
        ) : (
          <table className="aud-table">
            <thead>
              <tr>
                <th style={{ width: 148 }}>Fecha / Hora</th>
                <th style={{ width: 160 }}>Usuario</th>
                <th style={{ width: 100 }}>Acción</th>
                <th style={{ width: 120 }}>Módulo</th>
                <th>Descripción</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const ac = ACTION_COLORS[log.action] ?? ACTION_COLORS.viewed;
                return (
                  <tr key={log.id}>
                    <td className="aud-td-date">{fmtDate(log.created_at)}</td>
                    <td className="aud-td-user">
                      <div className="aud-user-cell">
                        <span className="aud-avatar">{(log.user_name[0] ?? '?').toUpperCase()}</span>
                        <span>{log.user_name}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className="aud-action-badge"
                        style={{ color: ac.color, background: ac.bg, borderColor: ac.color + '40' }}
                      >
                        {ACTION_LABELS[log.action] ?? log.action}
                      </span>
                    </td>
                    <td className="aud-td-module">
                      <ModuleChip type={log.entity_type} />
                    </td>
                    <td className="aud-td-desc">{buildDescription(log)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && hasMore && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
            <button className="aud-btn-clear-sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Cargando…' : `Cargar más (${summary.total - logs.length} restantes)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Module chip ───────────────────────────────────────────────────────────────

const MODULE_CHIP_COLORS: Record<string, { color: string; bg: string }> = {
  product:    { color: '#22c55e', bg: 'rgba(34,197,94,0.1)'   },
  warehouse:  { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)'  },
  zone:       { color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)'  },
  user:       { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)'  },
  invitation: { color: '#ec4899', bg: 'rgba(236,72,153,0.1)'  },
};

function ModuleChip({ type }: { type: string }) {
  const c = MODULE_CHIP_COLORS[type] ?? { color: '#64748b', bg: 'rgba(100,116,139,0.1)' };
  return (
    <span className="aud-module-chip" style={{ color: c.color, background: c.bg, borderColor: c.color + '40' }}>
      {MODULE_LABELS[type] ?? (type === 'auth' ? AUTH_LABEL : type)}
    </span>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function PdfIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="12" y2="17" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function HistoryIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
